import React from 'react';
import { showSuccessNotification, showErrorNotification, showInfoNotification, showWarningNotification } from '../components/NotificationManager';
import { showLocalNotification } from './notificationUtil';

// Notification types
export const NOTIFICATION_TYPES = {
  ORDER_PLACED: 'ORDER_PLACED',
  ORDER_CONFIRMED: 'ORDER_CONFIRMED',
  ORDER_READY: 'ORDER_READY',
  ORDER_DELIVERED: 'ORDER_DELIVERED',
  PAYMENT_RECEIVED: 'PAYMENT_RECEIVED',
  SPECIAL_OFFER: 'SPECIAL_OFFER',
  DELIVERY_UPDATE: 'DELIVERY_UPDATE',
};

// Notification icons mapping
const NOTIFICATION_ICONS = {
  [NOTIFICATION_TYPES.ORDER_PLACED]: '/icons/pizza.png',
  [NOTIFICATION_TYPES.ORDER_CONFIRMED]: '/icons/pizza.png',
  [NOTIFICATION_TYPES.ORDER_READY]: '/icons/pizza.png',
  [NOTIFICATION_TYPES.ORDER_DELIVERED]: '/icons/pizza.png',
  [NOTIFICATION_TYPES.PAYMENT_RECEIVED]: '/icons/pizza.png',
  [NOTIFICATION_TYPES.SPECIAL_OFFER]: '/icons/pizza.png',
  [NOTIFICATION_TYPES.DELIVERY_UPDATE]: '/icons/pizza.png',
};

/**
 * Show a notification both as in-app notification and push notification if possible
 * @param {string} title - Notification title
 * @param {string} message - Notification message
 * @param {string} type - Notification type from NOTIFICATION_TYPES
 * @param {object} options - Additional options for the notification
 */
export const showNotification = async (title, message, type, options = {}) => {
  // Map notification type to EnhancedNotification type
  const notificationTypeMap = {
    [NOTIFICATION_TYPES.ORDER_PLACED]: 'info',
    [NOTIFICATION_TYPES.ORDER_CONFIRMED]: 'success',
    [NOTIFICATION_TYPES.ORDER_READY]: 'success',
    [NOTIFICATION_TYPES.ORDER_DELIVERED]: 'success',
    [NOTIFICATION_TYPES.PAYMENT_RECEIVED]: 'warning',
    [NOTIFICATION_TYPES.SPECIAL_OFFER]: 'success',
    [NOTIFICATION_TYPES.DELIVERY_UPDATE]: 'info',
  };

  // Select the appropriate notification function
  const notificationFnMap = {
    success: showSuccessNotification,
    error: showErrorNotification,
    warning: showWarningNotification,
    info: showInfoNotification,
  };

  const notificationType = notificationTypeMap[type] || 'info';
  const notificationFn = notificationFnMap[notificationType];

  // Show in-app notification using EnhancedNotification
  notificationFn({
    title,
    message,
    type: notificationType,
    duration: options.duration || 5000,
    actions: options.actions?.map(action => ({
      label: action.title,
      type: action.action === 'view' ? 'view_order' : action.action,
      route: options.url,
      ...action
    })) || [
      {
        label: 'View Details',
        type: 'view_order',
        route: options.url || '/',
      },
      {
        label: 'Close',
        type: 'close',
      },
    ],
    autoClose: options.autoClose !== false,
  });

  // If the user has granted notification permission, show a push notification
  if (Notification.permission === 'granted') {
    try {
      // Try to use the service worker to show a notification
      const registration = await navigator.serviceWorker.ready;

      await registration.showNotification(title, {
        body: message,
        icon: NOTIFICATION_ICONS[type] || '/pizza.png',
        badge: '/favicon.ico',
        vibrate: [100, 50, 100],
        data: {
          url: options.url || '/',
          type: type,
          ...options.data,
        },
        actions: options.actions || [
          {
            action: 'view',
            title: 'View Details',
          },
          {
            action: 'close',
            title: 'Close',
          },
        ],
      });
    } catch (error) {
      // Fallback to local notification if service worker is not available
      showLocalNotification(title, {
        body: message,
        icon: NOTIFICATION_ICONS[type] || '/pizza.png',
      });

      console.error('Error showing push notification:', error);
    }
  }
};

/**
 * Show an order status notification
 * @param {object} order - Order object with id, status, and other details
 */
export const showOrderStatusNotification = (order) => {
  let title, message, type, options;

  switch (order.status) {
    case 'Pending':
      title = 'Order Pending';
      message = `Your order #${order.id} is awaiting confirmation.`;
      type = NOTIFICATION_TYPES.ORDER_PLACED;
      options = {
        url: `/user/order/${order.id}`,
        data: { orderId: order.id },
      };
      break;

    case 'Confirmed':
      title = 'Order Confirmed';
      message = `Restaurant has confirmed your order #${order.id}. It's being prepared now!`;
      type = NOTIFICATION_TYPES.ORDER_CONFIRMED;
      options = {
        url: `/user/order/${order.id}`,
        data: { orderId: order.id },
      };
      break;

    case 'Fee Requested':
      title = 'Payment Required';
      message = `Additional fee requested for order #${order.id}. Please complete payment.`;
      type = NOTIFICATION_TYPES.PAYMENT_RECEIVED;
      options = {
        url: `/user/order/${order.id}`,
        data: { orderId: order.id },
        actions: [
          {
            action: 'payment',
            title: 'Make Payment',
          },
          {
            action: 'close',
            title: 'Close',
          },
        ],
      };
      break;

    case 'Delivered':
      title = 'Order Delivered';
      message = `Your order #${order.id} has been delivered. Enjoy your meal!`;
      type = NOTIFICATION_TYPES.ORDER_DELIVERED;
      options = {
        url: `/user/order/${order.id}`,
        data: { orderId: order.id },
        actions: [
          {
            action: 'view_receipt',
            title: 'View Receipt',
          },
          {
            action: 'reorder',
            title: 'Reorder',
          },
          {
            action: 'close',
            title: 'Close',
          },
        ],
      };
      break;

    case 'Canceled':
      title = 'Order Canceled';
      message = `Your order #${order.id} has been canceled.`;
      type = NOTIFICATION_TYPES.DELIVERY_UPDATE;
      options = {
        url: `/user/order/${order.id}`,
        data: { orderId: order.id },
        actions: [
          {
            action: 'order_history',
            title: 'View Order History',
          },
          {
            action: 'close',
            title: 'Close',
          },
        ],
      };
      break;

    default:
      title = 'Order Update';
      message = `Your order #${order.id} status has been updated to ${order.status}.`;
      type = NOTIFICATION_TYPES.DELIVERY_UPDATE;
      options = {
        url: `/user/order/${order.id}`,
        data: { orderId: order.id },
      };
  }

  showNotification(title, message, type, options);
};

/**
 * Show a special offer notification
 * @param {object} offer - Offer details
 */
export const showSpecialOfferNotification = (offer) => {
  const title = 'Special Offer';
  const message = offer.description || 'Check out this special offer just for you!';

  showNotification(title, message, NOTIFICATION_TYPES.SPECIAL_OFFER, {
    url: offer.url || '/user',
    data: { offerId: offer.id },
    actions: [
      {
        action: 'view',
        title: 'See Offer',
      },
      {
        action: 'close',
        title: 'Not Now',
      },
    ],
  });
};